import React, { useState, useEffect } from 'react';
import { BomberosHeader } from '../../components/BomberosHeader';
import { useNavigate, useParams } from 'react-router-dom';
import { bomberosPersonalAPI } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Progress } from '../../components/ui/progress';
import { toast } from 'sonner';
import {
    ArrowLeft, Edit, User, Phone, Mail, MapPin, Calendar, Award,
    HardHat, GraduationCap, Shield, Droplets, AlertTriangle,
    CheckCircle, XCircle, Clock, Building2, FileDown,
} from 'lucide-react';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

export default function BomberoDetallePage() {
    const navigate = useNavigate();
    const { id } = useParams();
    const [loading, setLoading] = useState(true);
    const [bombero, setBombero] = useState(null);
    const [capacitaciones, setCapacitaciones] = useState([]);

    useEffect(() => {
        if (id) loadData();
    }, [id]);

    const loadData = async () => {
        setLoading(true);
        try {
            const [bomberoRes, capsRes] = await Promise.all([
                bomberosPersonalAPI.getById(id),
                bomberosPersonalAPI.getCapacitaciones(id).catch(() => ({ data: [] })),
            ]);
            setBombero(bomberoRes.data);
            setCapacitaciones(capsRes.data || []);
        } catch (err) {
            console.error('Error loading data:', err);
            toast.error('Error al cargar datos del bombero');
            navigate('/bomberos/personal');
        } finally {
            setLoading(false);
        }
    };

    const getGradoBadgeColor = (grado) => {
        const map = {
            comandante: 'bg-red-500 text-white',
            capitan: 'bg-orange-500 text-white',
            teniente: 'bg-yellow-500 text-black',
            bombero: 'bg-blue-500 text-white',
            voluntario: 'bg-green-500 text-white',
        };
        return map[grado] || 'bg-gray-500 text-white';
    };

    const getEstadoEPP = (estado) => {
        const map = {
            disponible: { color: 'text-green-600', label: 'Disponible' },
            asignado: { color: 'text-blue-600', label: 'Asignado' },
            mantenimiento: { color: 'text-yellow-600', label: 'Mantenimiento' },
            baja: { color: 'text-red-600', label: 'Baja' },
        };
        return map[estado] || { color: 'text-gray-600', label: estado };
    };

    const calcularAntiguedad = (fechaIngreso) => {
        if (!fechaIngreso) return 'N/A';
        const ingreso = new Date(fechaIngreso);
        const hoy = new Date();
        const años = Math.floor((hoy - ingreso) / (365.25 * 24 * 60 * 60 * 1000));
        const meses = Math.floor(((hoy - ingreso) % (365.25 * 24 * 60 * 60 * 1000)) / (30.44 * 24 * 60 * 60 * 1000));
        if (años > 0) return `${años} año${años > 1 ? 's' : ''} ${meses} mes${meses !== 1 ? 'es' : ''}`;
        return `${meses} mes${meses !== 1 ? 'es' : ''}`;
    };

    const getEstadoCapBadge = (estado) => {
        const map = {
            finalizada: 'default',
            en_curso: 'secondary',
            programada: 'outline',
            cancelada: 'destructive',
        };
        return map[estado] || 'outline';
    };

    if (loading) {
        return (
            <div className="space-y-6 p-6">
                <Skeleton className="h-10 w-64" />
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    <Skeleton className="h-64" />
                    <Skeleton className="h-64 lg:col-span-2" />
                </div>
            </div>
        );
    }

    if (!bombero) {
        return (
            <div className="p-6">
                <p className="text-center text-muted-foreground">Bombero no encontrado</p>
            </div>
        );
    }

    const competencias = bombero.competencias || [];
    const eppAsignado = bombero.epp_asignado || [];

    const exportarFichaPDF = () => {
        const doc = new jsPDF();
        const fecha = new Date().toLocaleDateString('es-CL');
        let y = 15;

        // Header
        doc.setFontSize(18);
        doc.text('Ficha de Bombero', 14, y);
        y += 8;
        doc.setFontSize(10);
        doc.setTextColor(128);
        doc.text(`Generado: ${fecha}`, 14, y);
        doc.setTextColor(0);
        y += 12;

        // Datos personales
        doc.setFontSize(13);
        doc.text('Datos Personales', 14, y);
        y += 5;

        autoTable(doc, {
            startY: y,
            theme: 'grid',
            headStyles: { fillColor: [41, 128, 185], fontSize: 9 },
            bodyStyles: { fontSize: 9 },
            body: [
                ['Nombre Completo', bombero.nombre_completo],
                ['RUT', bombero.rut],
                ['Grado', (bombero.grado || '').charAt(0).toUpperCase() + (bombero.grado || '').slice(1)],
                ['Cargo', bombero.cargo || 'N/A'],
                ['Compañía', bombero.compania_nombre || 'N/A'],
                ['Fecha Ingreso', bombero.fecha_ingreso || 'N/A'],
                ['Fecha Nacimiento', bombero.fecha_nacimiento || 'N/A'],
                ['Antigüedad', calcularAntiguedad(bombero.fecha_ingreso)],
                ['Grupo Sanguíneo', bombero.grupo_sanguineo || 'N/A'],
                ['Email', bombero.email || 'N/A'],
                ['Teléfono', bombero.telefono || 'N/A'],
                ['Dirección', bombero.direccion || 'N/A'],
                ['Contacto Emergencia', bombero.contacto_emergencia || 'N/A'],
                ['Tel. Emergencia', bombero.telefono_emergencia || 'N/A'],
                ['Estado', bombero.activo ? 'Activo' : 'Inactivo'],
            ],
            columnStyles: { 0: { fontStyle: 'bold', cellWidth: 50 } },
            margin: { left: 14, right: 14 },
        });
        y = doc.lastAutoTable.finalY + 12;

        // Competencias
        if (competencias.length > 0) {
            doc.setFontSize(13);
            doc.text('Competencias', 14, y);
            y += 3;
            autoTable(doc, {
                startY: y,
                head: [['Competencia', 'Nivel', 'Vigencia', 'Fecha Obtención']],
                body: competencias.map(c => [
                    c.nombre || c.competencia_id,
                    c.nivel || 'N/A',
                    c.vigente !== false ? 'Vigente' : 'Vencida',
                    c.fecha_obtencion || 'N/A',
                ]),
                theme: 'grid',
                headStyles: { fillColor: [52, 152, 219], fontSize: 8 },
                bodyStyles: { fontSize: 8 },
                margin: { left: 14, right: 14 },
            });
            y = doc.lastAutoTable.finalY + 10;
        }

        // EPP
        if (eppAsignado.length > 0) {
            if (y > 240) { doc.addPage(); y = 15; }
            doc.setFontSize(13);
            doc.text('EPP Asignado', 14, y);
            y += 3;
            autoTable(doc, {
                startY: y,
                head: [['Tipo', 'Código', 'Marca', 'Talla', 'Estado']],
                body: eppAsignado.map(e => [
                    (e.tipo || '').replace('_', ' '),
                    e.codigo || '',
                    e.marca || '',
                    e.talla || '',
                    e.estado || '',
                ]),
                theme: 'grid',
                headStyles: { fillColor: [243, 156, 18], fontSize: 8 },
                bodyStyles: { fontSize: 8 },
                margin: { left: 14, right: 14 },
            });
            y = doc.lastAutoTable.finalY + 10;
        }

        // Capacitaciones
        if (capacitaciones.length > 0) {
            if (y > 220) { doc.addPage(); y = 15; }
            doc.setFontSize(13);
            doc.text('Historial de Capacitaciones', 14, y);
            y += 3;
            autoTable(doc, {
                startY: y,
                head: [['Nombre', 'Código', 'Estado', 'Asistencia', 'Evaluación']],
                body: capacitaciones.map(c => [
                    c.nombre,
                    c.codigo || '',
                    c.estado || '',
                    c.sesiones_totales > 0 ? `${c.porcentaje_asistencia}% (${c.sesiones_asistidas}/${c.sesiones_totales})` : 'N/A',
                    c.evaluacion ? `${c.evaluacion.resultado}${c.evaluacion.nota != null ? ` (${c.evaluacion.nota})` : ''}` : 'N/A',
                ]),
                theme: 'grid',
                headStyles: { fillColor: [155, 89, 182], fontSize: 8 },
                bodyStyles: { fontSize: 8 },
                margin: { left: 14, right: 14 },
            });
        }

        const rut = (bombero.rut || '').replace(/\./g, '').replace('-', '');
        doc.save(`ficha_bombero_${rut}_${new Date().toISOString().slice(0,10)}.pdf`);
        toast.success('Ficha PDF generada');
    };

    return (
        <>
            <BomberosHeader />
            <div className="space-y-6 p-6 pt-20 md:pt-20">
                {/* Header */}
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                        <Button variant="ghost" size="icon" onClick={() => navigate('/bomberos/personal')} data-testid="back-btn">
                            <ArrowLeft className="h-5 w-5" />
                        </Button>
                        <div>
                            <h1 className="text-2xl font-bold" data-testid="bombero-nombre">{bombero.nombre_completo}</h1>
                            <p className="text-muted-foreground flex items-center gap-2">
                                <Badge className={`${getGradoBadgeColor(bombero.grado)} capitalize`} data-testid="bombero-grado">
                                    {bombero.grado}
                                </Badge>
                                {bombero.cargo && <span>· {bombero.cargo}</span>}
                            </p>
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        <Button variant="outline" onClick={exportarFichaPDF} data-testid="export-pdf-btn">
                            <FileDown className="h-4 w-4 mr-2" />
                            Ficha PDF
                        </Button>
                        <Button onClick={() => navigate(`/bomberos/personal/${id}/editar`)} data-testid="edit-btn">
                            <Edit className="h-4 w-4 mr-2" />
                            Editar
                        </Button>
                    </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    {/* Perfil */}
                    <Card className="border-2">
                        <CardHeader>
                            <CardTitle className="text-lg flex items-center gap-2">
                                <User className="h-5 w-5" />
                                Perfil
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <div className="flex items-center justify-center mb-4">
                                <div className="w-24 h-24 rounded-full bg-primary/10 flex items-center justify-center text-3xl font-bold text-primary">
                                    {bombero.nombres?.[0]}{bombero.apellido_paterno?.[0]}
                                </div>
                            </div>
                            <div className="space-y-3 text-sm">
                                <div className="flex items-center gap-3">
                                    <Shield className="h-4 w-4 text-muted-foreground" />
                                    <span className="text-muted-foreground">RUT:</span>
                                    <span className="font-mono">{bombero.rut}</span>
                                </div>
                                <div className="flex items-center gap-3">
                                    <Building2 className="h-4 w-4 text-muted-foreground" />
                                    <span className="text-muted-foreground">Compañía:</span>
                                    <span>{bombero.compania_nombre}</span>
                                </div>
                                <div className="flex items-center gap-3">
                                    <Calendar className="h-4 w-4 text-muted-foreground" />
                                    <span className="text-muted-foreground">Ingreso:</span>
                                    <span>{bombero.fecha_ingreso}</span>
                                </div>
                                <div className="flex items-center gap-3">
                                    <Clock className="h-4 w-4 text-muted-foreground" />
                                    <span className="text-muted-foreground">Antigüedad:</span>
                                    <span>{calcularAntiguedad(bombero.fecha_ingreso)}</span>
                                </div>
                                {bombero.grupo_sanguineo && (
                                    <div className="flex items-center gap-3">
                                        <Droplets className="h-4 w-4 text-muted-foreground" />
                                        <span className="text-muted-foreground">Grupo:</span>
                                        <Badge variant="outline" className="text-red-600 border-red-600">
                                            {bombero.grupo_sanguineo}
                                        </Badge>
                                    </div>
                                )}
                            </div>
                            <div className="pt-4 border-t space-y-2">
                                <h4 className="font-medium text-sm">Contacto</h4>
                                {bombero.email && (
                                    <div className="flex items-center gap-2 text-sm">
                                        <Mail className="h-4 w-4 text-muted-foreground" />
                                        <a href={`mailto:${bombero.email}`} className="text-primary hover:underline">{bombero.email}</a>
                                    </div>
                                )}
                                {bombero.telefono && (
                                    <div className="flex items-center gap-2 text-sm">
                                        <Phone className="h-4 w-4 text-muted-foreground" />
                                        <a href={`tel:${bombero.telefono}`} className="text-primary hover:underline">{bombero.telefono}</a>
                                    </div>
                                )}
                                {bombero.direccion && (
                                    <div className="flex items-center gap-2 text-sm">
                                        <MapPin className="h-4 w-4 text-muted-foreground" />
                                        <span>{bombero.direccion}</span>
                                    </div>
                                )}
                            </div>
                            {bombero.contacto_emergencia && (
                                <div className="pt-4 border-t space-y-2">
                                    <h4 className="font-medium text-sm flex items-center gap-2">
                                        <AlertTriangle className="h-4 w-4 text-orange-500" />
                                        Contacto de Emergencia
                                    </h4>
                                    <p className="text-sm">{bombero.contacto_emergencia}</p>
                                    {bombero.telefono_emergencia && (
                                        <p className="text-sm text-muted-foreground">{bombero.telefono_emergencia}</p>
                                    )}
                                </div>
                            )}
                            <div className="pt-4 border-t">
                                <Badge
                                    variant={bombero.activo ? "default" : "destructive"}
                                    className="w-full justify-center py-2"
                                    data-testid="bombero-estado"
                                >
                                    {bombero.activo ? (
                                        <><CheckCircle className="h-4 w-4 mr-2" /> Activo</>
                                    ) : (
                                        <><XCircle className="h-4 w-4 mr-2" /> Inactivo</>
                                    )}
                                </Badge>
                            </div>
                        </CardContent>
                    </Card>

                    {/* Tabs */}
                    <div className="lg:col-span-2">
                        <Tabs defaultValue="competencias" className="w-full">
                            <TabsList className="grid w-full grid-cols-3">
                                <TabsTrigger value="competencias" className="flex items-center gap-2" data-testid="tab-competencias">
                                    <Award className="h-4 w-4" />
                                    <span className="hidden sm:inline">Competencias</span>
                                    {competencias.length > 0 && (
                                        <Badge variant="secondary" className="ml-1 h-5 px-1.5 text-xs">{competencias.length}</Badge>
                                    )}
                                </TabsTrigger>
                                <TabsTrigger value="epp" className="flex items-center gap-2" data-testid="tab-epp">
                                    <HardHat className="h-4 w-4" />
                                    <span className="hidden sm:inline">EPP</span>
                                    {eppAsignado.length > 0 && (
                                        <Badge variant="secondary" className="ml-1 h-5 px-1.5 text-xs">{eppAsignado.length}</Badge>
                                    )}
                                </TabsTrigger>
                                <TabsTrigger value="capacitaciones" className="flex items-center gap-2" data-testid="tab-capacitaciones">
                                    <GraduationCap className="h-4 w-4" />
                                    <span className="hidden sm:inline">Capacitaciones</span>
                                    {capacitaciones.length > 0 && (
                                        <Badge variant="secondary" className="ml-1 h-5 px-1.5 text-xs">{capacitaciones.length}</Badge>
                                    )}
                                </TabsTrigger>
                            </TabsList>

                            {/* Competencias */}
                            <TabsContent value="competencias">
                                <Card className="border-2">
                                    <CardHeader>
                                        <CardTitle className="text-lg">Competencias y Especialidades</CardTitle>
                                        <CardDescription>Certificaciones y habilidades del bombero</CardDescription>
                                    </CardHeader>
                                    <CardContent>
                                        {competencias.length === 0 ? (
                                            <div className="text-center py-8 text-muted-foreground" data-testid="competencias-empty">
                                                <Award className="h-12 w-12 mx-auto mb-4 opacity-50" />
                                                <p>No hay competencias registradas</p>
                                                <p className="text-sm mt-2">Las competencias se agregan al aprobar capacitaciones</p>
                                            </div>
                                        ) : (
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="competencias-list">
                                                {competencias.map((comp, idx) => (
                                                    <div key={idx} className="p-4 rounded-lg border bg-muted/50">
                                                        <div className="flex items-center justify-between mb-2">
                                                            <h4 className="font-medium">{comp.nombre || comp.competencia_id}</h4>
                                                            <Badge variant={comp.vigente !== false ? "default" : "secondary"}>
                                                                {comp.vigente !== false ? 'Vigente' : 'Vencida'}
                                                            </Badge>
                                                        </div>
                                                        {comp.nivel && (
                                                            <p className="text-xs text-muted-foreground capitalize mb-1">
                                                                Nivel: {comp.nivel}
                                                            </p>
                                                        )}
                                                        <p className="text-xs text-muted-foreground">
                                                            Obtenida: {comp.fecha_obtencion || 'N/A'}
                                                        </p>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </CardContent>
                                </Card>
                            </TabsContent>

                            {/* EPP Asignado */}
                            <TabsContent value="epp">
                                <Card className="border-2">
                                    <CardHeader>
                                        <CardTitle className="text-lg">EPP Asignado</CardTitle>
                                        <CardDescription>Equipamiento de protección personal</CardDescription>
                                    </CardHeader>
                                    <CardContent>
                                        {eppAsignado.length === 0 ? (
                                            <div className="text-center py-8 text-muted-foreground" data-testid="epp-empty">
                                                <HardHat className="h-12 w-12 mx-auto mb-4 opacity-50" />
                                                <p>No tiene EPP asignado</p>
                                            </div>
                                        ) : (
                                            <div className="space-y-3" data-testid="epp-list">
                                                {eppAsignado.map((item, idx) => {
                                                    const estadoConfig = getEstadoEPP(item.estado);
                                                    return (
                                                        <div key={item.id || idx} className="flex items-center justify-between p-3 rounded-lg border">
                                                            <div className="flex items-center gap-3">
                                                                <HardHat className="h-5 w-5 text-muted-foreground" />
                                                                <div>
                                                                    <p className="font-medium capitalize">{(item.tipo || '').replace('_', ' ')}</p>
                                                                    <p className="text-xs text-muted-foreground">
                                                                        {item.codigo} · {item.marca || 'Sin marca'} {item.talla && `· Talla ${item.talla}`}
                                                                    </p>
                                                                </div>
                                                            </div>
                                                            <Badge variant="outline" className={estadoConfig.color}>
                                                                {estadoConfig.label}
                                                            </Badge>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        )}
                                    </CardContent>
                                </Card>
                            </TabsContent>

                            {/* Capacitaciones */}
                            <TabsContent value="capacitaciones">
                                <Card className="border-2">
                                    <CardHeader>
                                        <CardTitle className="text-lg">Historial de Capacitaciones</CardTitle>
                                        <CardDescription>Cursos y entrenamientos completados e inscritos</CardDescription>
                                    </CardHeader>
                                    <CardContent>
                                        {capacitaciones.length === 0 ? (
                                            <div className="text-center py-8 text-muted-foreground" data-testid="capacitaciones-empty">
                                                <GraduationCap className="h-12 w-12 mx-auto mb-4 opacity-50" />
                                                <p>No hay capacitaciones registradas</p>
                                                <p className="text-sm mt-2">El historial aparecerá cuando se registre asistencia o inscripción</p>
                                            </div>
                                        ) : (
                                            <div className="space-y-3" data-testid="capacitaciones-list">
                                                {capacitaciones.map((cap) => (
                                                    <div
                                                        key={cap.id}
                                                        className="p-4 rounded-lg border hover:bg-muted/50 cursor-pointer transition-colors"
                                                        onClick={() => navigate(`/bomberos/capacitaciones/${cap.id}`)}
                                                        data-testid={`cap-item-${cap.codigo}`}
                                                    >
                                                        <div className="flex items-center justify-between mb-2">
                                                            <div>
                                                                <h4 className="font-medium">{cap.nombre}</h4>
                                                                <p className="text-xs text-muted-foreground">
                                                                    {cap.codigo} · {cap.fecha_inicio} → {cap.fecha_fin} · {cap.horas_duracion}h
                                                                </p>
                                                            </div>
                                                            <Badge variant={getEstadoCapBadge(cap.estado)} className="capitalize">
                                                                {(cap.estado || '').replace('_', ' ')}
                                                            </Badge>
                                                        </div>
                                                        {cap.sesiones_totales > 0 && (
                                                            <div className="mt-2">
                                                                <div className="flex items-center justify-between text-xs text-muted-foreground mb-1">
                                                                    <span>Asistencia: {cap.sesiones_asistidas}/{cap.sesiones_totales} sesiones</span>
                                                                    <span className="font-medium">{cap.porcentaje_asistencia}%</span>
                                                                </div>
                                                                <Progress value={cap.porcentaje_asistencia} className="h-2" />
                                                            </div>
                                                        )}
                                                        {cap.evaluacion && (
                                                            <div className="mt-2 flex items-center gap-2">
                                                                <Badge
                                                                    variant={cap.evaluacion.resultado === 'aprobado' ? 'default' : cap.evaluacion.resultado === 'reprobado' ? 'destructive' : 'secondary'}
                                                                    className="capitalize"
                                                                >
                                                                    {cap.evaluacion.resultado}
                                                                </Badge>
                                                                {cap.evaluacion.nota != null && (
                                                                    <span className="text-xs text-muted-foreground">Nota: {cap.evaluacion.nota}</span>
                                                                )}
                                                            </div>
                                                        )}
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </CardContent>
                                </Card>
                            </TabsContent>
                        </Tabs>
                    </div>
                </div>
            </div>
        </>
    );
}
